import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { UserPlus, Mail, Lock, User, AlertCircle, Zap } from 'lucide-react';
import GoogleSignInButton from '../components/GoogleSignInButton';
import AppleSignInButton from '../components/AppleSignInButton';

export default function RegisterPage() {
  const [mode, setMode] = useState('username'); // 'username' or 'email'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, registerWithUsername } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    let result;

    if (mode === 'username') {
      if (username.trim().length < 3) {
        setError('Username must be at least 3 characters');
        setLoading(false);
        return;
      }
      result = await registerWithUsername(username.trim(), password);
    } else {
      result = await register(email, password, name || email.split('@')[0]);
    }

    setLoading(false);

    if (result.success) {
      navigate('/play');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--background-main)' }}>
      <Card className="w-full max-w-md bg-[var(--background-surface)] border-[var(--border-subtle)]">
        <CardHeader className="text-center">
          <Link to="/" className="inline-block mb-4">
            <h1 className="font-heading text-2xl font-bold text-[var(--text-primary)]">Sudoku Academy</h1>
          </Link>
          <CardTitle className="text-2xl font-heading text-[var(--text-primary)]">Create Account</CardTitle>
          <CardDescription className="text-[var(--text-secondary)]">
            Start your journey to Sudoku mastery
          </CardDescription>
        </CardHeader>
        <CardContent>
          <GoogleSignInButton label="Sign up with Google" testId="register-google-signin" />
          <div className="mt-3">
            <AppleSignInButton label="Sign up with Apple" testId="register-apple-signin" />
          </div>
          <div className="flex items-center gap-3 my-5" role="separator">
            <div className="flex-1 h-px bg-[var(--border-subtle)]" />
            <span className="text-xs uppercase tracking-wider text-[var(--text-disabled)]">or</span>
            <div className="flex-1 h-px bg-[var(--border-subtle)]" />
          </div>
          {/* Mode Toggle */}
          <div className="flex gap-1 p-1 rounded-lg bg-[var(--background-elevated)] border border-[var(--border-subtle)] mb-5">
            <button
              type="button"
              onClick={() => { setMode('username'); setError(''); }}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                mode === 'username'
                  ? 'bg-[#8B5A3C] text-white'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
              data-testid="register-mode-username"
            >
              <Zap className="w-3.5 h-3.5" />
              Quick Sign Up
            </button>
            <button
              type="button"
              onClick={() => { setMode('email'); setError(''); }}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                mode === 'email'
                  ? 'bg-[#8B5A3C] text-white'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
              data-testid="register-mode-email"
            >
              <Mail className="w-3.5 h-3.5" />
              With Email
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="register-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {mode === 'username' ? (
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[var(--text-secondary)]">Username</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-disabled)]" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="Choose a username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 bg-[var(--background-elevated)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)]"
                    required
                    minLength={3}
                    maxLength={20}
                    data-testid="register-username"
                  />
                </div>
                <p className="text-xs text-[var(--text-disabled)]">3-20 characters, letters, numbers, hyphens, underscores</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[var(--text-secondary)]">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-disabled)]" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 bg-[var(--background-elevated)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)]"
                      required
                      data-testid="register-name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[var(--text-secondary)]">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-disabled)]" />
                    <Input
                      id="email"
                      type="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      inputMode="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-[var(--background-elevated)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)]"
                      required
                      data-testid="register-email"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[var(--text-secondary)]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-disabled)]" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[var(--background-elevated)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)]"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
              </div>
              <p className="text-xs text-[var(--text-disabled)]">Minimum 6 characters</p>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#8B5A3C] hover:bg-[#A46944] text-white font-semibold"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  {mode === 'username' ? 'Start Playing' : 'Create Account'}
                </span>
              )}
            </Button>

            {mode === 'username' && (
              <p className="text-xs text-center text-[var(--text-disabled)]">
                No email required. You can add one later for password recovery.
              </p>
            )}
          </form>

          <div className="mt-6 text-center text-sm text-[var(--text-secondary)]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#8B5A3C] hover:text-[#A46944] font-medium">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
